import { useFragment } from "@/generated";
import { EditProjectNameDocument, ProjectFieldsFragmentDoc } from "@/generated/graphql";
import { useMutation } from "@apollo/client/react";

/**
 * Hook for renaming an existing project.
 *
 * Wraps the `editProjectName` GraphQL mutation and automatically refetches
 * related project queries to keep the UI up to date after the mutation completes.
 *
 * @returns An object with:
 *  - editProjectName: (projectId: string, name: string) => Promise<Project | undefined>
 *      Renames the project with the provided ID and returns the updated project.
 *  - loading: boolean
 *      Indicates whether the mutation is currently in progress.
 *
 * @example
 * const { editProjectName, loading } = useEditProjectName();
 *
 * await editProjectName("project123", "New Project Name");
 */
export function useEditProjectName() {
  const [editProjectNameMutation, { loading }] = useMutation(EditProjectNameDocument);

  const editProjectName = async (projectId: string, name: string) => {
    const result = await editProjectNameMutation({
      variables: { projectId, name },
    });

    const maskedProject = result.data?.editProjectName;
    const updatedProject = maskedProject
      ? useFragment(ProjectFieldsFragmentDoc, maskedProject)
      : undefined;

    return updatedProject;
  };

  return {
    editProjectName,
    loading,
  };
}
